import { useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

// LuxKit Components
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import Clickable from 'components/Common/Clickable'
import { buildSearchParamsForFilterPane } from 'components/Pages/DestinationLandingPage/DestinationLandingPageUtils'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { mediaQueryUp } from 'components/utils/breakpoint'
import clsx from 'clsx'
import MaldivesDirectSearchFlightCalendarItemMobile from './MaldivesDirectSearchFlightCalendarItemMobile'
import MaldivesOfferCalendarItemMobile from './MaldivesOfferCalendarItemMobile'
import { useAppSelector } from 'hooks/reduxHooks'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getOfferListKey from 'lib/offer/offerListKey'
import { getEffectiveFlightPrice, getFlightSearchKey, isFnfPriceActive } from 'lib/flights/flightUtils'
import { buildFlightSearchPayload, buildBundleOfferFilters } from '../MaldivesDirectUtils'
import { HOTEL_OFFERS_LIMIT, MALDIVES_CHARTER_CONFIG, KIDS_FLY_FREE_COLOUR } from 'constants/charterFlight'
import KidsFlyFreeLabel from 'components/Common/Labels/KidsFlyFreeLabel'
import useKidsFlyFree from 'hooks/CharterFlights/useKidsFlyFree'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import LineKidIcon from 'LuxKit/Icons/line/LineKidIcon'
import Places from 'constants/places'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { isRewardFareClass, FlightsClassTypes, type FareClassWithReward } from 'constants/flight'
import { toBaseFareClass } from 'lib/flights/rewardFareClasses'
import { CHARTER_CASH_CABINS } from 'lib/maldivesCharter/charterCabins'
import useMaldivesCharterModalFareClasses from 'hooks/Flights/useMaldivesCharterModalFareClasses'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import { REWARD_PREVIEW_RATE_BY_CABIN } from 'lib/maldivesCharter/classicRewardPricing'
import CurrencyContext from 'contexts/currencyContext'
import type { CalendarItemBundleData } from '../hooks/types'
import type { MaldivesCabinHotelEntry } from '../hooks/useMaldivesOffersPricing'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import { getLowestAvailableHotelPrice, useSeatEligiblePassengers } from '../hooks/utils'
import useAllCharterFlightJourneys from 'hooks/CharterFlights/useAllCharterFlightJourneys'
import useCharterRewardSeatsIndicatorForDate from '../hooks/useCharterRewardSeatsIndicatorForDate'
import useFeatureFlag from 'hooks/Optimizely/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import {
  CharterRewardAvailabilityContext,
  type CabinDateJourneys,
} from '../hooks/charterRewardAvailabilityContext'
import { aggregateMaldivesCharterMonthTileRewardPricing } from './maldivesCharterMonthTileRewardPricing'
import type { CharterRewardLeg } from 'hooks/Flights/useCharterRewardAvailability'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import { charterCalendarDayKey } from 'lib/maldivesCharter/charterCalendarDayKey'
import { INTL_LONG_MONTH, INTL_SHORT_MONTH } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'

// Types
interface DateRange {
  departureDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  durationDays: number // Total days from departureDate to returnDate
  nights: number // Nights from checkIn to checkOut
}

interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
  fareClass?: FareClassWithReward
}

interface Props {
  onDateSelect: (option: TripOption) => void
  selectedOptionId?: string
  nights: number
  occupancies?: Array<App.Occupants>
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  luxPlusEnabled: boolean
  dateRangesByMonth: Record<string, Array<DateRange>>
  selectedMonth: string
  onMonthChange: (month: string) => void
  /** Ready-to-use calendar item data - no calculation needed **/
  itemBundleDataByDate?: Record<string, CalendarItemBundleData>
  mode: 'search' | 'offer'
  /** When mode is 'offer', pass the offer to show Kids Fly Free label only when offer has campaign tag */
  offer?: App.HotelOffer
  /** The user's selected fare class including reward variants; drives per-cell reward pricing. */
  selectedFareClass?: FareClassWithReward | null
  /** Callback when the user picks a different fare class from the carousel. */
  onFareClassChange?: (fareClass: FareClassWithReward) => void
  /**
   * Optional cabin filter from the modal's class dropdown. When set, the per-date fare
   * carousel only shows variants (cash + reward) of that base cabin. Independent from
   * `selectedFareClass` which represents the user's confirmed pick, not a UI filter.
   */
  cabinFilter?: FlightsClassTypes | null
  hotelPriceByCabinAndDate?: Partial<Record<FlightsClassTypes, Record<string, MaldivesCabinHotelEntry>>>
  /** When true, only show reward fare classes in the per-date fare carousel. */
  rewardSeatsOnly?: boolean
  /** Optional header actions to render at the top */
  headerActions?: React.ReactNode
  /** Optional scroll container ref for precise programmatic scrolling */
  scrollContainerRef?: React.RefObject<HTMLElement | null>
}

enum SelectionStep {
  MONTH = 'month',
  DATE = 'date',
}

const KidsFlyFreeIconColour = styled.span`
  color: ${KIDS_FLY_FREE_COLOUR};
  display: inline-flex;
`

const MonthsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  margin-top: ${rem(12)};
  column-gap: ${rem(8)};
  row-gap: ${rem(12)};
`

const MonthCard = styled(Clickable)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: none;
  transition: background-color 0.2s, box-shadow 0.2s;
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(12)};
  min-height: ${rem(70)};
  &:not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        box-shadow: ${props => props.theme.shadow.bottom.medium};
      }
    }
    &:focus {
      box-shadow: ${props => props.theme.shadow.bottom.medium};
    }
  }

  &.selected {
    border: 1px solid ${props => props.theme.palette.neutral.surface.darkForeground};
  }

  &.specialOffer {
    border-color: ${props => props.theme.palette.highlight.primary.normalForeground};
    border-width: 2px;
  }
`

const DateCardContainer = styled(VerticalSpacer)`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  margin: 0 -${rem(20)};
  border-radius: ${rem(8)};
`
const EmptyStateGroup = styled(Group)`
  padding: ${rem(32)};
`

const SpecialOfferBadge = styled.div`
  display: inline-block;
  padding: ${rem(4)} ${rem(8)};
  background-color: ${props => props.theme.palette.highlight.primary.normalForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  margin-left: ${rem(8)};
`

const MonthSection = styled(VerticalSpacer)`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  margin: 0 -${rem(20)};
  padding: 0 ${rem(20)};
  border-radius: ${props => props.theme.borderRadius.S};
  ${mediaQueryUp.tablet} {
    margin: 0 -${rem(24)};
  }
`

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  margin-inline: ${rem(-20)};
  padding-inline: ${rem(20)};
  padding-bottom: ${rem(8)};
`

const HeaderActionsRow = styled(HorizontalContainerWithFadedOverflow)`
  margin-bottom: ${rem(4)};
`

const BottomLinkContainer = styled.div`
  padding: ${rem(32)} 0 ${rem(16)};
  text-align: center;
`

const MonthHeaderGroup = styled(Group)`
  padding: ${rem(12)} 0;
`

const ClickableYearHeading = styled(Heading)`
  cursor: pointer;
  user-select: none;
  text-decoration: underline;
`

// Main Component
function MaldivesDirectSearchFlightCalendarMobile(props: Props) {
  const {
    onDateSelect,
    selectedOptionId,
    nights,
    occupancies,
    fareClass,
    priceMode,
    luxPlusEnabled,
    dateRangesByMonth,
    selectedMonth,
    onMonthChange,
    mode,
    itemBundleDataByDate,
    offer,
    selectedFareClass,
    onFareClassChange,
    cabinFilter,
    hotelPriceByCabinAndDate,
    rewardSeatsOnly = false,
    headerActions,
    scrollContainerRef,
  } = props

  const { kidsFlyFreeDates, hasKidsFlyFreeDateInMonth, shouldShowKidsFlyFreeForOffer } = useKidsFlyFree({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: occupancies,
    fareClass,
  })
  const [currentStep, setCurrentStep] = useState<SelectionStep>(SelectionStep.MONTH)
  const [targetMonthSectionKey, setTargetMonthSectionKey] = useState<string>()
  const monthSectionRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const rooms = useMemo(() => occupancies || [{ adults: 2, children: 0 }], [occupancies])
  const allowedFareClassesAll = useMaldivesCharterModalFareClasses(CHARTER_CASH_CABINS)
  const allowedFareClasses = useMemo(() => {
    let filtered = allowedFareClassesAll
    if (cabinFilter) {
      filtered = filtered.filter(fc => toBaseFareClass(fc) === cabinFilter)
    }
    if (rewardSeatsOnly) {
      filtered = filtered.filter(fc => isRewardFareClass(fc))
    }
    return filtered
  }, [allowedFareClassesAll, cabinFilter, rewardSeatsOnly])

  // Calculate total passengers for per-person pricing
  const totalPassengers = useMemo(
    () => sumUpOccupancies(rooms),
    [rooms],
  )
  const seatEligiblePassengers = useSeatEligiblePassengers(rooms)

  // Fetch all cabin journeys so the fare carousel can show prices for every cabin,
  // matching the desktop trip-options table. Calls are cached at the Redux level.
  const economyJourneys = useAllCharterFlightJourneys({ charterBundleRoute: MALDIVES_CHARTER_ROUTE, occupancies: rooms, fareClass: FlightsClassTypes.ECONOMY })
  const premiumJourneys = useAllCharterFlightJourneys({ charterBundleRoute: MALDIVES_CHARTER_ROUTE, occupancies: rooms, fareClass: FlightsClassTypes.PREMIUM })
  const businessJourneys = useAllCharterFlightJourneys({ charterBundleRoute: MALDIVES_CHARTER_ROUTE, occupancies: rooms, fareClass: FlightsClassTypes.BUSINESS })

  const { rewardLegs, rewardLegsByDate } = useMemo(() => {
    const legs: Array<CharterRewardLeg> = []
    const legsByDate: Record<string, Array<string>> = {}
    const seenJourneyKeys = new Set<string>()

    // Use the selected cabin for legsByDate (drives per-date all-or-nothing indicator),
    // but flatten all cabins into legs so getLegResult works for any cabin's journey key
    // (matching desktop pattern).
    const pageCabin = CHARTER_CASH_CABINS.includes(fareClass) ? fareClass : FlightsClassTypes.ECONOMY
    const allCabinFares: Array<[FlightsClassTypes, ReturnType<typeof useAllCharterFlightJourneys>]> = [
      [FlightsClassTypes.ECONOMY, economyJourneys],
      [FlightsClassTypes.PREMIUM, premiumJourneys],
      [FlightsClassTypes.BUSINESS, businessJourneys],
    ]
    const pageFares = allCabinFares.find(([c]) => c === pageCabin)?.[1]
    const pageDeparting = pageFares?.fares?.[0] ?? []
    const pageReturning = pageFares?.fares?.[1] ?? []

    Object.values(dateRangesByMonth).forEach((dateRanges) => {
      dateRanges.forEach(({ checkIn, checkOut }) => {
        // legsByDate for the selected cabin (drives month-tile seal)
        const dep = pageDeparting.find((j) => charterCalendarDayKey(j.flightGroup.flights.at(-1)?.arrivalDate) === checkIn)
        const ret = pageReturning.find((j) => charterCalendarDayKey(j.flightGroup.flights.at(0)?.departingDate) === checkOut)
        if (dep?.journeyKey && ret?.journeyKey) {
          legsByDate[checkIn] = [dep.journeyKey, ret.journeyKey]
        }

        // Flatten across all cabins for leg coverage
        allCabinFares.forEach(([, fares]) => {
          const departing = fares?.fares?.[0] ?? []
          const returning = fares?.fares?.[1] ?? []
          const d = departing.find((j) => charterCalendarDayKey(j.flightGroup.flights.at(-1)?.arrivalDate) === checkIn)
          const r = returning.find((j) => charterCalendarDayKey(j.flightGroup.flights.at(0)?.departingDate) === checkOut)
          for (const journey of [d, r]) {
            const key = journey?.journeyKey
            if (!key || seenJourneyKeys.has(key)) continue
            seenJourneyKeys.add(key)
            legs.push({ journeyKey: key, legTotal: journey.price.all.totalFare ?? 0 })
          }
        })
      })
    })

    return { rewardLegs: legs, rewardLegsByDate: legsByDate }
  }, [economyJourneys, premiumJourneys, businessJourneys, dateRangesByMonth, fareClass])

  const isRewardSeatsEnabled = useFeatureFlag(OptimizelyFeatureFlags.maldivesCharterRewardSeats)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const rewardIndicator = useCharterRewardSeatsIndicatorForDate({
    legs: rewardLegs,
    legsByDate: rewardLegsByDate,
    passengerCount: seatEligiblePassengers,
    disabled: !isRewardSeatsEnabled || !hasLoyaltyAccount || rewardLegs.length === 0,
  })

  // Build per-cabin per-date journey map so the fare carousel can resolve prices for all cabins,
  // matching the desktop trip-options table pattern.
  const journeysByCabinDate = useMemo(() => {
    const map = new Map<FlightsClassTypes, Map<string, CabinDateJourneys>>()
    const cabinFares: Array<[FlightsClassTypes, ReturnType<typeof useAllCharterFlightJourneys>]> = [
      [FlightsClassTypes.ECONOMY, economyJourneys],
      [FlightsClassTypes.PREMIUM, premiumJourneys],
      [FlightsClassTypes.BUSINESS, businessJourneys],
    ]
    cabinFares.forEach(([cabin, fares]) => {
      const cabinMap = new Map<string, CabinDateJourneys>()
      const departingJourneys = fares?.fares?.[0] ?? []
      const returningJourneys = fares?.fares?.[1] ?? []
      Object.values(dateRangesByMonth).forEach((dateRanges) => {
        dateRanges.forEach(({ checkIn, checkOut }) => {
          const departing = departingJourneys.find((j) =>
            charterCalendarDayKey(j.flightGroup.flights.at(-1)?.arrivalDate) === checkIn,
          )
          const returning = returningJourneys.find((j) =>
            charterCalendarDayKey(j.flightGroup.flights.at(0)?.departingDate) === checkOut,
          )
          cabinMap.set(checkIn, { departing, returning })
        })
      })
      map.set(cabin, cabinMap)
    })
    return map
  }, [economyJourneys, premiumJourneys, businessJourneys, dateRangesByMonth])

  // Keep a shortcut for the selected cabin (used by reward indicator + month-tile pricing).
  // Memoised so it is referentially stable when the underlying map and fareClass don't change,
  // preventing downstream useMemo dependents from re-running every render.
  const getJourneysForCabinDate = useCallback(
    (cabin: FlightsClassTypes, dateKey: string): CabinDateJourneys | undefined => {
      return journeysByCabinDate.get(cabin)?.get(dateKey)
    },
    [journeysByCabinDate],
  )

  const charterRewardContextValue = useMemo(() => ({
    ...rewardIndicator,
    getJourneysForCabinDate,
  }), [rewardIndicator, getJourneysForCabinDate])

  const memberId = useAppSelector(state => state.auth.account.memberId)
  const rewardLockedForLogin = rewardSeatsOnly && (!memberId || !hasLoyaltyAccount)

  // Hardcoded preview rates for logged-out / non-member users: cheapest cabin rate + cheapest hotel per month
  const rewardPreviewByMonth = useMemo(() => {
    if (!rewardLockedForLogin) return {}
    const cabinsToCheck = cabinFilter ? [cabinFilter] : [...CHARTER_CASH_CABINS]
    let cheapestRate: { pointsPerPerson: number; cashPerPerson: number } | undefined
    for (const cabin of cabinsToCheck) {
      const rate = REWARD_PREVIEW_RATE_BY_CABIN[cabin]
      if (rate && (!cheapestRate || rate.cashPerPerson < cheapestRate.cashPerPerson)) {
        cheapestRate = rate
      }
    }
    if (!cheapestRate) return {}
    const flightPoints = cheapestRate.pointsPerPerson * seatEligiblePassengers
    const flightCash = cheapestRate.cashPerPerson * seatEligiblePassengers

    const result: Record<string, { points: number; cash: number }> = {}
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      let cheapestHotel: number | undefined
      dateRanges.forEach(({ checkIn }) => {
        for (const cabin of cabinsToCheck) {
          const h = hotelPriceByCabinAndDate?.[cabin]?.[checkIn]
          const hp = h ? (luxPlusEnabled ? (h.hotelMemberPrice || h.hotelPrice || 0) : (h.hotelPrice || 0)) : 0
          if (hp > 0 && (cheapestHotel === undefined || hp < cheapestHotel)) {
            cheapestHotel = hp
          }
        }
        if (cheapestHotel === undefined) {
          const itemData = itemBundleDataByDate?.[checkIn]
          if (itemData?.available && itemData.hotelPrice && itemData.hotelPrice > 0) {
            if (cheapestHotel === undefined || itemData.hotelPrice < cheapestHotel) {
              cheapestHotel = itemData.hotelPrice
            }
          }
        }
      })
      if (cheapestHotel !== undefined) {
        const rawPoints = flightPoints
        const rawCash = cheapestHotel + flightCash
        result[monthName] = {
          points: priceMode === 'perPerson' && totalPassengers > 0 ? rawPoints / totalPassengers : rawPoints,
          cash: priceMode === 'perPerson' && totalPassengers > 0 ? rawCash / totalPassengers : rawCash,
        }
      }
    })
    return result
  }, [rewardLockedForLogin, cabinFilter, seatEligiblePassengers, dateRangesByMonth, hotelPriceByCabinAndDate, itemBundleDataByDate, luxPlusEnabled, priceMode, totalPassengers])

  // Cheapest reward price per month — same logic as desktop month tiles:
  // scan visible cabins (cabinFilter or all) for the cheapest reward bundle.
  const monthRewardPricing = useMemo(() => {
    if (!rewardSeatsOnly) return {}
    const cabinsToScan = cabinFilter ? [cabinFilter] : [...CHARTER_CASH_CABINS]
    const result: Record<string, { points: number; cash: number }> = {}
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      let lowestCash: number | undefined
      let correspondingPoints: number | undefined
      dateRanges.forEach(({ checkIn }) => {
        const itemData = itemBundleDataByDate?.[checkIn]
        for (const cabin of cabinsToScan) {
          const entry = journeysByCabinDate.get(cabin)?.get(checkIn)
          if (!entry?.departing || !entry?.returning) continue

          if (!entry.departing.journeyKey || !entry.returning.journeyKey) continue
          const depResult = rewardIndicator.getLegResult(entry.departing.journeyKey)
          const retResult = rewardIndicator.getLegResult(entry.returning.journeyKey)
          if (!depResult?.available || !retResult?.available) continue
          if (depResult.pointsPrice == null || retResult.pointsPrice == null) continue
          if (depResult.cashPrice == null || retResult.cashPrice == null) continue

          // Derive hotel price: prefer hotelPriceByCabinAndDate (same as desktop), fallback to bundle
          const cabinHotel = hotelPriceByCabinAndDate?.[cabin]?.[checkIn]
          let hotelForReward = 0
          if (cabinHotel) {
            hotelForReward = luxPlusEnabled
              ? (cabinHotel.hotelMemberPrice || cabinHotel.hotelPrice || 0)
              : (cabinHotel.hotelPrice || 0)
          } else if (itemData?.available) {
            const cabinFlightFare = (entry.departing.price?.all?.totalFare ?? 0) + (entry.returning.price?.all?.totalFare ?? 0)
            const bundleTotal = luxPlusEnabled ? (itemData.memberTotalPrice || itemData.totalPrice || 0) : (itemData.totalPrice || 0)
            hotelForReward = Math.max(0, bundleTotal - cabinFlightFare)
          }
          if (!hotelForReward) continue

          const rawPoints = (depResult.pointsPrice + retResult.pointsPrice) * seatEligiblePassengers
          const rawCash = (depResult.cashPrice + retResult.cashPrice) * seatEligiblePassengers + hotelForReward
          const totalPoints = priceMode === 'perPerson' && totalPassengers > 0 ? rawPoints / totalPassengers : rawPoints
          const totalCash = priceMode === 'perPerson' && totalPassengers > 0 ? rawCash / totalPassengers : rawCash

          if (lowestCash === undefined || totalCash < lowestCash) {
            lowestCash = totalCash
            correspondingPoints = totalPoints
          }
        }
      })
      if (correspondingPoints !== undefined && lowestCash !== undefined) {
        result[monthName] = { points: correspondingPoints, cash: lowestCash }
      }
    })
    return result
  }, [rewardSeatsOnly, cabinFilter, dateRangesByMonth, itemBundleDataByDate, journeysByCabinDate, seatEligiblePassengers, rewardIndicator, hotelPriceByCabinAndDate, luxPlusEnabled, priceMode, totalPassengers])

  const monthRewardSeals = useMemo(() => {
    // When cabinFilter is active, check only that cabin's reward availability.
    // Otherwise check ANY cabin so users discover reward inventory before picking a fare class.
    const cabinsToCheck = cabinFilter ? [cabinFilter] : [...CHARTER_CASH_CABINS]
    const datesByMonth: Record<string, Array<{ dateKey: string; allLegsAvailable: boolean }>> = {}
    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      datesByMonth[monthName] = dateRanges.map(({ checkIn }) => {
        const anyCabinAvailable = cabinsToCheck.some((cabin) => {
          const entry = journeysByCabinDate.get(cabin)?.get(checkIn)
          const depKey = entry?.departing?.journeyKey
          const retKey = entry?.returning?.journeyKey
          if (!depKey || !retKey) return false
          return rewardIndicator.getLegResult(depKey)?.available === true
            && rewardIndicator.getLegResult(retKey)?.available === true
        })
        return { dateKey: checkIn, allLegsAvailable: anyCabinAvailable }
      })
    })
    return aggregateMaldivesCharterMonthTileRewardPricing({ datesByMonth })
  }, [dateRangesByMonth, journeysByCabinDate, rewardIndicator, cabinFilter])

  const currencyContext = useContext(CurrencyContext)

  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()

  const allOfferLists = useAppSelector(state => state.offer.offerLists)
  const allOffers = useAppSelector(state => state.offer.offers)
  const allFlights = useAppSelector(state => state.flights.searchV2Flights)
  const allOfferMetaData = useAppSelector(state => state.offer.searchResultMetadata.offerMetaData)

  const flightScheduleText = intl.formatMessage({
    id: 'UBLSvzstf26qr4nVsKhx',
    defaultMessage: 'Flights depart Sunday night and arrive on Monday for hotel check-in.',
    description: 'Description of Maldives charter flight schedule in the flights section',
  })
  const specialOfferText = intl.formatMessage({
    id: 'Ena6iZ87hWIVOm8QPUB9',
    defaultMessage: 'Friends & Family Offer',
    description: 'Badge label for months in the charter flight calendar that include FnF pricing',
  })
  const loadingText = intl.formatMessage({
    id: '0GUG7eQ8fAeuOBChFIQt',
    defaultMessage: 'Loading…',
    description: 'Loading state text for charter flight month price in the flights section',
  })
  const unavailableText = intl.formatMessage({
    id: 'OlOfqYtNbU_4z3O0cdFc',
    defaultMessage: 'Unavailable',
    description: 'Text shown when no charter flights available for month',
  })
  const noSeatsText = intl.formatMessage({
    id: '0wEFPYrYYrDgvTpVmJ8N',
    defaultMessage: 'No seats available',
    description: 'Short label in the Maldives charter flight calendar when a fare column has no reward-seat inventory or no seats for that cabin.',
  })
  const lookingForOtherDatesText = intl.formatMessage({
    id: '-iMMCVN0aC5Ds5vZE85O',
    defaultMessage: 'Looking for other dates?',
    description: 'Prompt text linking to broader Maldives search in the flights section',
  })
  const exploreMoreText = intl.formatMessage({
    id: '4BIeJuy-1ltmZIF0LLP5',
    defaultMessage: 'Explore more Maldives offers.',
    description: 'Link text to explore more Maldives offers on the offer page',
  })
  const noDeparturesText = intl.formatMessage({
    id: 'GEmDdTUKT3QSyk6-6evz',
    defaultMessage: 'No departures available',
    description: 'Empty state heading when no charter departures found',
  })
  const tryDifferentMonthText = intl.formatMessage({
    id: 'Y6tOL00_SjJjhu7gDuDY',
    defaultMessage: 'Try selecting a different month',
    description: 'Help text when no charter departures available in the flights section',
  })

  // Generate months grouped by year with pricing from Redux
  const monthsByYear = useMemo(() => {
    const yearGroups: Record<number, Array<{
      name: string
      price: number
      fetching: boolean
      hasSpecialOffer: boolean
      monthLabel: string
      monthIndex: number
      year: number
    }>> = {}

    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      // Skip months with no date entries
      if (dateRanges.length === 0) return

      let lowestPrice = 0
      let isMonthFetching = false
      let hasSpecialOffer = false

      dateRanges.forEach(({ departureDate, returnDate, checkIn, checkOut }) => {
        if (itemBundleDataByDate && !cabinFilter) {
          const itemData = itemBundleDataByDate[checkIn]

          // Check if still fetching
          if (itemData?.fetching) {
            isMonthFetching = true
          }

          // Calculate bundle price based on priceMode and luxPlusEnabled
          const bundlePrice = priceMode === 'total'
            ? (luxPlusEnabled ? (itemData?.memberTotalPrice || itemData?.totalPrice) : itemData?.totalPrice)
            : (luxPlusEnabled ? (itemData?.memberPricePerPerson || itemData?.pricePerPerson) : itemData?.pricePerPerson)

          if (itemData?.available && bundlePrice && bundlePrice > 0) {
            hasSpecialOffer = hasSpecialOffer || !!itemData.isFnfPriceApplied

            if (lowestPrice === 0 || bundlePrice < lowestPrice) {
              lowestPrice = bundlePrice
            }
          }
        } else if (cabinFilter || mode === 'search') {
          // Per-cabin pricing — same as desktop: use hotelPriceByCabinAndDate + per-cabin journeys
          if (cabinFilter) {
            const cabinEntry = journeysByCabinDate.get(cabinFilter)?.get(checkIn)
            const depFare = cabinEntry?.departing?.price?.all?.totalFare ?? 0
            const retFare = cabinEntry?.returning?.price?.all?.totalFare ?? 0
            const totalFlightFare = depFare + retFare

            const cabinHotel = hotelPriceByCabinAndDate?.[cabinFilter]?.[checkIn]
            let hotelPrice = 0
            if (cabinHotel) {
              hotelPrice = luxPlusEnabled
                ? (cabinHotel.hotelMemberPrice || cabinHotel.hotelPrice || 0)
                : (cabinHotel.hotelPrice || 0)
            }

            if (totalFlightFare > 0 && hotelPrice > 0) {
              const bundleTotal = hotelPrice + totalFlightFare
              const bundlePrice = priceMode === 'perPerson' && totalPassengers > 0
                ? bundleTotal / totalPassengers
                : bundleTotal
              if (bundlePrice > 0 && (lowestPrice === 0 || bundlePrice < lowestPrice)) {
                lowestPrice = bundlePrice
              }
            }
          } else if (mode === 'search') {
          // Use checkIn/checkOut (inner dates) for hotel queries
            const hotelFilters = buildBundleOfferFilters({
              destinationId: Places.Maldives.id,
              destinationName: Places.Maldives.name,
              checkIn: checkIn,
              checkOut: checkOut,
              rooms: rooms,
              limit: HOTEL_OFFERS_LIMIT,
            })

            // Use departureDate/returnDate (outer dates) for flight queries
            const flightParams = buildFlightSearchPayload({
              departureAirport: MALDIVES_CHARTER_CONFIG.DEPARTURE_AIRPORT,
              arrivalAirport: MALDIVES_CHARTER_CONFIG.ARRIVAL_AIRPORT,
              departureDate: departureDate,
              returnDate: returnDate,
              occupancies: rooms,
              fareClass: fareClass,
              currency: currencyContext || 'AUD',
            })

            const hotelKey = getOfferListKey(hotelFilters)
            const flightKey = getFlightSearchKey(flightParams)

            const offerList = allOfferLists[hotelKey]
            const flightData = allFlights[flightKey]

            // Check if still fetching
            if (offerList?.fetching || flightData?.fetching) {
              isMonthFetching = true
            }

            // Calculate hotel price - only from available offers with valid capacity
            const offerIds = offerList?.offerIds || []
            const offerMetaDataForList = allOfferMetaData[hotelKey]

            const lowestHotelPrice = getLowestAvailableHotelPrice({
              offerIds,
              allOffers,
              offerMetaData: offerMetaDataForList,
              rooms,
              nights,
              luxPlusEnabled,
            })

            // Calculate flight price
            const departingFlight = flightData?.fares?.[0]?.[0]
            const returningFlight = flightData?.fares?.[1]?.[0]
            const departingFare = departingFlight ? getEffectiveFlightPrice(departingFlight, seatEligiblePassengers).all.totalFare ?? 0 : 0
            const returningFare = returningFlight ? getEffectiveFlightPrice(returningFlight, seatEligiblePassengers).all.totalFare ?? 0 : 0
            const totalFlightFare = departingFare + returningFare
            const isFnfPriceApplied = !!(
              departingFlight &&
              returningFlight &&
              isFnfPriceActive(departingFlight, seatEligiblePassengers) &&
              isFnfPriceActive(returningFlight, seatEligiblePassengers)
            )

            // Calculate bundle price based on price mode
            if (lowestHotelPrice > 0 && totalFlightFare > 0 && !offerList?.fetching && !flightData?.fetching) {
              const bundleTotal = lowestHotelPrice + totalFlightFare
              const bundlePrice = priceMode === 'perPerson' && totalPassengers > 0
                ? bundleTotal / totalPassengers
                : bundleTotal

              if (bundlePrice > 0 && (lowestPrice === 0 || bundlePrice < lowestPrice)) {
                lowestPrice = bundlePrice
              }

              hasSpecialOffer = hasSpecialOffer || isFnfPriceApplied
            }
          }
        }
      })

      const firstDate = dateRanges[0]?.checkIn
      const date = moment(firstDate)
      const year = date.year()
      const monthIndex = date.month()

      if (!yearGroups[year]) {
        yearGroups[year] = []
      }

      yearGroups[year].push({
        name: monthName,
        price: lowestPrice || 0,
        fetching: isMonthFetching,
        hasSpecialOffer,
        monthLabel: dateFormatter(date, INTL_SHORT_MONTH),
        monthIndex,
        year: year,
      })
    })

    // Sort months within each year
    Object.keys(yearGroups).forEach(year => {
      yearGroups[parseInt(year)].sort((a, b) => a.monthIndex - b.monthIndex)
    })

    return yearGroups
  }, [dateFormatter, dateRangesByMonth, allOfferLists, allOffers, allFlights, allOfferMetaData, luxPlusEnabled, nights, rooms, fareClass, priceMode, totalPassengers, seatEligiblePassengers, currencyContext, mode, itemBundleDataByDate, cabinFilter, journeysByCabinDate, hotelPriceByCabinAndDate])

  // Generate trip options for all months (grouped by month for rendering)
  const allMonthOptions = useMemo(() => {
    const allOptions: Array<{
      id: string
      checkIn: string
      checkOut: string
      departDate: string
      returnDate: string
      month: string
    }> = []

    Object.entries(dateRangesByMonth).forEach(([monthName, dateRanges]) => {
      // Skip months with no date entries
      if (dateRanges.length === 0) return

      dateRanges.forEach(({ departureDate, returnDate, checkIn, checkOut }) => {
        allOptions.push({
          id: `${checkIn}-${nights}n`,
          checkIn,
          checkOut,
          departDate: departureDate,
          returnDate: returnDate,
          month: monthName,
        })
      })
    })

    return allOptions
  }, [dateRangesByMonth, nights])

  // Group options by month for rendering
  const optionsByMonth = useMemo(() => {
    const groups: Record<string, Array<{
      id: string
      checkIn: string
      checkOut: string
      departDate: string
      returnDate: string
      month: string
    }>> = {}

    allMonthOptions.forEach((option) => {
      const monthKey = moment(option.checkIn).format('YYYY-MM')
      if (!groups[monthKey]) {
        groups[monthKey] = []
      }
      groups[monthKey].push(option)
    })

    // Sort months chronologically
    const sortedEntries = Object.entries(groups).sort(([a], [b]) => {
      return a.localeCompare(b)
    })

    return Object.fromEntries(sortedEntries)
  }, [allMonthOptions])

  // Handlers
  const handleMonthSelect = useCallback((monthName: string, monthKey: string) => {
    onMonthChange(monthName)
    setTargetMonthSectionKey(monthKey)
    setCurrentStep(SelectionStep.DATE)
  }, [onMonthChange])

  const handleBackToMonthSelect = useCallback(() => {
    setCurrentStep(SelectionStep.MONTH)
  }, [])

  const scrollContainerEl = scrollContainerRef?.current

  useLayoutEffect(() => {
    if (currentStep !== SelectionStep.DATE) {
      return
    }

    const resolvedKey = targetMonthSectionKey ?? Object.entries(optionsByMonth).find(([, options]) => {
      const optionMonth = options[0]?.month
      return optionMonth === selectedMonth
    })?.[0] ?? Object.keys(optionsByMonth)[0]

    if (!resolvedKey) {
      return
    }

    const monthElement = monthSectionRefs.current[resolvedKey]
    if (!monthElement) {
      return
    }

    if (scrollContainerEl) {
      const containerRect = scrollContainerEl.getBoundingClientRect()
      const monthRect = monthElement.getBoundingClientRect()
      const currentScrollTop = scrollContainerEl.scrollTop ?? 0
      const offset = monthRect.top - containerRect.top + currentScrollTop - 16
      scrollContainerEl.scrollTo({
        top: Math.max(offset, 0),
        behavior: 'auto',
      })
    } else {
      monthElement.scrollIntoView({
        behavior: 'auto',
        block: 'start',
      })
    }

    setTargetMonthSectionKey(undefined)
  }, [currentStep, targetMonthSectionKey, optionsByMonth, selectedMonth, scrollContainerEl])

  const renderHeader = (headerActions: React.ReactNode) => (
    <>
      <BodyText variant="medium" colour="light-foreground-alt">
        {flightScheduleText}
      </BodyText>
      <StickyHeader>
        <HeaderActionsRow overflowGutter={20} gap={8}>
          {headerActions}
        </HeaderActionsRow>
      </StickyHeader>
    </>
  )
  // Render month selection step
  const renderMonthSelection = () => {
    const sortedYears = Object.keys(monthsByYear).map(Number).sort()
    const showKidsFlyFreeInMonthStep = kidsFlyFreeDates.size > 0 && (mode !== 'offer' || !offer || shouldShowKidsFlyFreeForOffer(offer))

    return (
      <VerticalSpacer gap={12}>
        {showKidsFlyFreeInMonthStep && <KidsFlyFreeLabel variant="short" />}
        {sortedYears.map((year) => (
          <div key={year}>
            <Heading variant="heading5">{year}</Heading>
            <MonthsGrid>
              {monthsByYear[year].map((month) => {
                const priceColour = luxPlusEnabled ? 'lux-plus' : undefined
                const monthSectionKey = `${year}-${String(month.monthIndex + 1).padStart(2, '0')}`
                const monthHasKidsFlyFree = hasKidsFlyFreeDateInMonth(year, month.monthIndex)
                let monthBadge: React.ReactNode = null
                if (month.hasSpecialOffer) {
                  monthBadge = (
                    <SpecialOfferBadge>
                      <Caption variant="small" colour="highlight-primary-contrast">{specialOfferText}</Caption>
                    </SpecialOfferBadge>
                  )
                }

                return (
                  <MonthCard
                    key={month.name}
                    onClick={() => handleMonthSelect(month.name, monthSectionKey)}
                    className={clsx({
                      selected: selectedMonth === month.name,
                      specialOffer: month.hasSpecialOffer,
                    })}
                  >
                    <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
                      <Group direction="horizontal" verticalAlign="center" gap={4}>
                        <Heading variant="heading6">{month.monthLabel}</Heading>
                        {showKidsFlyFreeInMonthStep && monthHasKidsFlyFree && <KidsFlyFreeIconColour><LineKidIcon size="XS" /></KidsFlyFreeIconColour>}
                        {isRewardSeatsEnabled && monthRewardSeals[month.name]?.showRewardSeal && <ClassicRewardsSealIcon size={16} colour="highlight-primary-dark" aria-hidden />}
                      </Group>
                      {monthBadge}
                    </Group>
                    <VerticalSpacer gap={2}>
                      {month.fetching && <Caption variant="small">{loadingText}</Caption>}
                      {!month.fetching && rewardLockedForLogin && rewardPreviewByMonth[month.name] && (
                        <>
                          <Caption variant="large" colour="neutral-three">{intl.formatMessage({
                            id: '61Gm0owvNXkEHBOdcx8J',
                            defaultMessage: 'From',
                            description: 'Price prefix label in Maldives flight calendar item',
                          })}
                          </Caption>
                          <BodyText variant="large" weight="bold" colour={priceColour}>
                            <FormatLoyaltyPoints abbreviated points={rewardPreviewByMonth[month.name].points} />
                          </BodyText>
                          <Caption variant="small" colour={priceColour ?? 'neutral-two'} weight="normal">
                            <FormattedMessage
                              id="peHFjMVc2YKKQdYS2mU5"
                              defaultMessage="+ {cash}"
                              description="Cash co-pay portion of a Société reward-seat bundle in the Maldives charter calendar, shown under the points total in a smaller line with a leading plus sign before the formatted currency amount."
                              values={{ cash: <FormatCurrency value={rewardPreviewByMonth[month.name].cash} format="roundedDollar" /> }}
                            />
                          </Caption>
                        </>
                      )}
                      {!month.fetching && rewardSeatsOnly && !rewardLockedForLogin && (() => {
                        const rp = monthRewardPricing[month.name]
                        if (rp) {
                          return (
                            <>
                              <Caption variant="large" colour="neutral-three">{intl.formatMessage({
                                id: '61Gm0owvNXkEHBOdcx8J',
                                defaultMessage: 'From',
                                description: 'Price prefix label in Maldives flight calendar item',
                              })}
                              </Caption>
                              <BodyText variant="large" weight="bold" colour="lux-plus">
                                <FormatLoyaltyPoints abbreviated points={rp.points} />
                              </BodyText>
                              <Caption variant="small" colour="lux-plus" weight="normal">
                                <FormattedMessage
                                  id="peHFjMVc2YKKQdYS2mU5"
                                  defaultMessage="+ {cash}"
                                  description="Cash co-pay portion of a Société reward-seat bundle in the Maldives charter calendar, shown under the points total in a smaller line with a leading plus sign before the formatted currency amount."
                                  values={{ cash: <FormatCurrency value={rp.cash} format="roundedDollar" /> }}
                                />
                              </Caption>
                            </>
                          )
                        }
                        return <Caption variant="small">{noSeatsText}</Caption>
                      })()}
                      {!month.fetching && !rewardSeatsOnly && month.price > 0 && (
                        <BodyText variant="large" weight="bold" colour={priceColour}>
                          <Caption variant="large" colour="light-foreground-alt">{intl.formatMessage({
                            id: '61Gm0owvNXkEHBOdcx8J',
                            defaultMessage: 'From',
                            description: 'Price prefix label in Maldives flight calendar item',
                          })}
                          </Caption>
                          <FormatCurrency value={month.price} format="roundedDollar" />
                        </BodyText>
                      )}
                      {!month.fetching && !rewardSeatsOnly && !month.price && <Caption variant="small">{unavailableText}</Caption>}
                    </VerticalSpacer>
                  </MonthCard>
                )
              })}
            </MonthsGrid>
          </div>
        ))}
        <BottomLinkContainer>
          <BodyText variant="small" colour="light-foreground-alt">
            {lookingForOtherDatesText}
          </BodyText>
          <TextLink
            size="small"
            weight="regular"
            target="_blank"
            to={`/search?${buildSearchParamsForFilterPane({
              originalSearch: '',
              destinationName: Places.Maldives.name,
              destinationId: Places.Maldives.id,
              adults: 2,
              children: 'none',
            })}`}
          >
            {exploreMoreText}
          </TextLink>
        </BottomLinkContainer>
      </VerticalSpacer>
    )
  }

  // Render date selection step - shows all months grouped by month/year
  const renderDateSelection = () => {
    if (allMonthOptions.length === 0) {
      return (
        <EmptyStateGroup direction="vertical" horizontalAlign="center" gap={8}>
          <Heading variant="heading5" align="center">
            {noDeparturesText}
          </Heading>
          <BodyText variant="medium" align="center" colour="light-foreground-alt">
            {tryDifferentMonthText}
          </BodyText>
        </EmptyStateGroup>
      )
    }

    return (
      <VerticalSpacer gap={24}>
        {Object.entries(optionsByMonth).map(([monthKey, options]) => {
          const monthDate = moment(monthKey, 'YYYY-MM')
          const month = dateFormatter(monthDate, INTL_LONG_MONTH)
          const year = dateFormatter(monthDate, { year: 'numeric' })

          return (
            <MonthSection
              key={monthKey}
              gap={0}
              ref={(el) => {
                if (monthKey) {
                  monthSectionRefs.current[monthKey] = el
                }
              }}
            >
              <MonthHeaderGroup
                direction="horizontal"
                horizontalAlign="space-between"
                verticalAlign="center"
              >
                <Heading variant="heading4">{month}</Heading>
                <ClickableYearHeading variant="heading4" onClick={handleBackToMonthSelect}>
                  {year}
                </ClickableYearHeading>
              </MonthHeaderGroup>
              <DateCardContainer gap={8}>
                {options.map((dateItem) => {
                  const commonProps = {
                    checkIn: dateItem.checkIn,
                    checkOut: dateItem.checkOut,
                    departDate: dateItem.departDate,
                    returnDate: dateItem.returnDate,
                    nights: nights,
                    priceMode: priceMode,
                    luxPlusEnabled: luxPlusEnabled,
                    isSelected: dateItem.id === selectedOptionId,
                    onSelect: onDateSelect,
                  }

                  switch (mode) {
                    case 'search':
                      return (
                        <MaldivesDirectSearchFlightCalendarItemMobile
                          key={dateItem.id}
                          {...commonProps}
                          rooms={rooms}
                          fareClass={fareClass}
                          selectedFareClass={selectedFareClass}
                          prefetchedBundleData={itemBundleDataByDate?.[dateItem.checkIn]}
                          allowedFareClasses={allowedFareClasses}
                          onFareClassChange={onFareClassChange}
                          rewardSeatsOnly={rewardSeatsOnly}
                          cabinFilter={cabinFilter}
                          hotelPriceByCabinAndDate={hotelPriceByCabinAndDate}
                        />
                      )
                    case 'offer': {
                      // Get pre-calculated bundle data for this date
                      const itemData = itemBundleDataByDate?.[dateItem.checkIn]

                      // Fallback to default empty state if data is missing
                      const itemBundleData: CalendarItemBundleData = itemData ?? {
                        fetching: true,
                        available: false,
                        totalPrice: 0,
                        pricePerPerson: 0,
                        offerCount: 0,
                        seatsLeft: 0,
                        hasNoPackage: false,
                      }

                      return (
                        <MaldivesOfferCalendarItemMobile
                          key={dateItem.id}
                          {...commonProps}
                          bundleData={itemBundleData}
                          rooms={rooms}
                          fareClass={fareClass}
                          offer={offer}
                        />
                      )
                    }
                    default:
                      return null
                  }
                })}
              </DateCardContainer>
            </MonthSection>
          )
        })}
      </VerticalSpacer>
    )
  }

  return (
    <CharterRewardAvailabilityContext.Provider value={charterRewardContextValue}>
      {renderHeader(headerActions)}
      <div style={{ display: currentStep === SelectionStep.MONTH ? 'block' : 'none' }}>
        {renderMonthSelection()}
      </div>
      <div style={{ display: currentStep === SelectionStep.DATE ? 'block' : 'none' }}>
        {renderDateSelection()}
      </div>
    </CharterRewardAvailabilityContext.Provider>
  )
}

export default MaldivesDirectSearchFlightCalendarMobile
